import ReactApexChart from 'react-apexcharts';
import { Box, Stack, Typography } from '@mui/material';
import { RT } from '../../../retention/theme/retentionTheme';
import { BANDS, BODY_FONT, DISPLAY_FONT, EmptyBlock, bandColor, fmtPct, numberSx } from './ui';

const base = {
  chart: { fontFamily: BODY_FONT, toolbar: { show: false }, zoom: { enabled: false }, animations: { speed: 500 } },
  grid: { borderColor: '#EEF0F6', strokeDashArray: 4, padding: { left: 6, right: 6 } },
  dataLabels: { enabled: false },
  legend: { fontFamily: BODY_FONT, fontWeight: 600, labels: { colors: RT.ink2 }, markers: { radius: 12 } },
  tooltip: { theme: 'light', style: { fontFamily: BODY_FONT } },
  xaxis: { axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: RT.muted, fontSize: '11.5px' } } },
};

const percentAxis = {
  min: 0,
  max: 100,
  tickAmount: 4,
  labels: { formatter: (value) => `${Math.round(value)}%`, style: { colors: RT.muted } },
};

// Sessions (columns) + attendance % (smooth line) over time. points = [{ label, sessions, attendance }]
export const TrendChart = ({ points = [], height = 300, sessionsLabel = 'Sessions completed' }) => (
  <ReactApexChart
    type="line"
    height={height}
    series={[
      { name: sessionsLabel, type: 'column', data: points.map((point) => point.sessions) },
      { name: 'Attendance %', type: 'area', data: points.map((point) => point.attendance ?? null) },
    ]}
    options={{
      ...base,
      colors: ['#C7D2FE', '#6D28D9'],
      stroke: { width: [0, 3], curve: 'smooth' },
      fill: {
        type: ['solid', 'gradient'],
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95, 100] },
      },
      markers: { size: [0, 4], strokeWidth: 2, strokeColors: '#fff', hover: { size: 6 } },
      plotOptions: { bar: { columnWidth: '42%', borderRadius: 6 } },
      labels: points.map((point) => point.label),
      xaxis: { ...base.xaxis, type: 'category' },
      yaxis: [
        { title: { text: 'Sessions', style: { color: RT.muted, fontWeight: 600 } }, labels: { style: { colors: RT.muted } }, min: 0, forceNiceScale: true },
        { opposite: true, ...percentAxis },
      ],
      tooltip: { ...base.tooltip, shared: true, intersect: false, y: { formatter: (value, { seriesIndex }) => (seriesIndex === 1 ? fmtPct(value) : value) } },
    }}
  />
);

// Attendance % per weekday, each bar coloured by band
export const WeekdayChart = ({ weekday = [], height = 240 }) => {
  const days = weekday.filter((slot) => slot.marked > 0 || slot.sessions > 0);
  if (!days.length) return <EmptyBlock title="No marks yet" />;
  return (
    <ReactApexChart
      type="bar"
      height={height}
      series={[{ name: 'Attendance %', data: days.map((slot) => slot.attendance ?? 0) }]}
      options={{
        ...base,
        colors: days.map((slot) => bandColor(slot.attendance)),
        plotOptions: { bar: { distributed: true, columnWidth: '48%', borderRadius: 8 } },
        legend: { show: false },
        xaxis: { ...base.xaxis, categories: days.map((slot) => slot.day) },
        yaxis: percentAxis,
        dataLabels: { enabled: true, formatter: (value) => fmtPct(value), offsetY: -18, style: { fontSize: '11px', colors: [RT.ink2] } },
        tooltip: {
          ...base.tooltip,
          y: { formatter: (value, { dataPointIndex }) => `${fmtPct(value)} · ${days[dataPointIndex].present}/${days[dataPointIndex].marked} present` },
        },
      }}
    />
  );
};

// Donut of students per attendance band
export const BandDonut = ({ bands = {}, height = 250 }) => {
  const shown = BANDS.filter((band) => (bands[band.key] || 0) > 0);
  const total = shown.reduce((sum, band) => sum + bands[band.key], 0);
  if (!total) return <EmptyBlock title="No marks yet" />;
  return (
    <ReactApexChart
      type="donut"
      height={height}
      series={shown.map((band) => bands[band.key])}
      options={{
        ...base,
        labels: shown.map((band) => `${band.label} (${band.range})`),
        colors: shown.map((band) => band.color),
        stroke: { width: 3, colors: ['#fff'] },
        legend: { ...base.legend, position: 'bottom', fontSize: '12px' },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                value: { fontFamily: DISPLAY_FONT, fontSize: '26px', fontWeight: 700, color: RT.ink },
                total: { show: true, label: 'Students', color: RT.muted, fontSize: '12px', formatter: () => total },
              },
            },
          },
        },
      }}
    />
  );
};

// Ranked horizontal bars: rows = [{ name, attendance, hint }]
export const RankBars = ({ rows = [], onClick }) => (
  <Stack gap={1.25}>
    {rows.map((row, index) => (
      <Stack
        key={`${row.name}-${index}`}
        direction="row"
        alignItems="center"
        gap={1.5}
        onClick={onClick ? () => onClick(row) : undefined}
        sx={{ cursor: onClick ? 'pointer' : 'default', borderRadius: 2, px: 0.5, py: 0.25, '&:hover': onClick ? { bgcolor: '#F6F7FB' } : undefined }}
      >
        <Box sx={{ width: { xs: 110, sm: 170 }, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: RT.ink }} noWrap title={row.name}>
            {row.name}
          </Typography>
          {row.hint && (
            <Typography variant="caption" sx={{ color: RT.faint }} noWrap component="div">
              {row.hint}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, height: 10, borderRadius: 99, bgcolor: '#EEF0F6', overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${Math.min(row.attendance || 0, 100)}%`,
              height: '100%',
              borderRadius: 99,
              background: `linear-gradient(90deg, ${bandColor(row.attendance)}99, ${bandColor(row.attendance)})`,
            }}
          />
        </Box>
        <Typography sx={{ width: 54, textAlign: 'right', fontWeight: 800, fontSize: 13, color: bandColor(row.attendance), ...numberSx }}>
          {fmtPct(row.attendance)}
        </Typography>
      </Stack>
    ))}
  </Stack>
);
